import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router";
import { useLastJob } from "@/api/queries";
import { CheckInModal, checkInPending } from "@/checkin/CheckInModal";
import { skipsCheckIn } from "@/checkin/checkin";
import { Copy } from "@/copy/copy";
import { DatePicker } from "@/components/DatePicker";
import { MoneyField, NumberField, SelectField, TextField } from "@/components/fields";
import { FirstTimeBuyerChecks } from "@/components/FirstTimeBuyer";
import { DebtRow, PropertyRow } from "@/components/OwedRows";
import { PlanJob_State } from "@/generated/backend/v1/plan";
import { AccountType, RRSPAccount_PlanType } from "@/generated/core/v1/accounts";
import { type Family, MaritalStatus, RetirementMethod } from "@/generated/core/v1/family";
import { IncomeKind, PensionType } from "@/generated/core/v1/person";
import type {
  AccountsSection_Account,
  HouseholdSection,
  IncomeSection_Income,
  SafetyNetSection_EmergencyFund,
  SafetyNetSection_PensionAnswer,
  SpendingSection,
} from "@/generated/core/v1/questionnaire";
import { ValueSource_Source } from "@/generated/core/v1/value_source";
import { copyText } from "@/copy/copy";
import { formatDate } from "@/plan-logic/lib/dates";
import { money } from "@/plan-logic/lib/format";
import { newUuid } from "@/plan-logic/lib/uuid";
import {
  emptyQuestionnaire,
  fieldEdit,
  firstTimeBuyerEdits,
  paths,
} from "@/plan-logic/questionnaire/build";
import {
  DEFAULT_RETIREMENT_PCT,
  type DraftDebt,
  type DraftProperty,
  INCOME_TYPE_HINT,
  INCOME_TYPE_LABEL,
  incomeType,
  isJobKind,
  kindForType,
  MATCHABLE_KINDS,
  newDebt,
  newProperty,
  PCT_OPTIONS,
  PROVINCES,
} from "@/plan-logic/questionnaire/draft";
import { FROM_SPENDING } from "@/priorities/PriorityEditor";
import {
  debtAnswer,
  debtRowsFromFamily,
  propertyAnswer,
  propertyRowsFromFamily,
  validateOwed,
} from "@/plan-logic/questionnaire/owed";
import {
  ACCOUNT_TYPE_LABEL,
  accountsFromFamily,
  householdSection,
  incomesFromFamily,
  pensionsFromFamily,
  retirementFromFamily,
  type RetirementModel,
  spendingFromFamily,
} from "@/plan-logic/questionnaire/fromFamily";
import {
  DEFAULT_RETIREMENT_AGE,
  householdRetirementCheck,
  retirementAgeOptions,
  type SavingAdult,
} from "@/questionnaire/retirementAge";
import { yearsInCanadaIssue } from "@/questionnaire/yearsInCanada";
import { PlanPage, type PlanCtx } from "../PlanPage";
import { FIRST_TIME_BOXES, SaveFeedback, useFamilySave } from "../useFamilySave";
import { isRebuilding, rebuildFailed } from "../../shell/PlanRebuildStatus";
import { GroupRrspOfferField } from "@/components/GroupRrspOfferField";
import { JobStartField } from "@/components/JobStartField";
import {
  jobStartDate,
  jobStartFrom,
  jobStartIssue,
  jobStartQuestion,
} from "@/plan-logic/questionnaire/jobStart";
import { groupRrspChoiceNeeded, groupRrspLinks } from "@/questionnaire/groupRrspLink";

type Model = {
  household: HouseholdSection;
  incomes: IncomeSection_Income[];
  accounts: AccountsSection_Account[];
  debts: DraftDebt[];
  properties: DraftProperty[];
  spending: SpendingSection;
  emergency: SafetyNetSection_EmergencyFund[];
  pensions: SafetyNetSection_PensionAnswer[];
  retirement: RetirementModel;
  /** persons[].is_first_time_home_buyer, asked beside the properties ("What you owe"). */
  firstTimeBuyer: Record<string, boolean>;
  /** persons[].oas_residency.years_in_canada_after_18 (retirement section), by person uuid. */
  oasYears: Record<string, number | undefined>;
};
type SectionKey =
  | "household"
  | "income"
  | "accounts"
  | "debts"
  | "spending"
  | "safety"
  | "retirement"
  | "oas"
  | "buyer";

function modelFrom(family: Family): Model {
  return {
    household: householdSection(family),
    incomes: incomesFromFamily(family),
    accounts: accountsFromFamily(family),
    debts: debtRowsFromFamily(family),
    properties: propertyRowsFromFamily(family),
    spending: spendingFromFamily(family),
    emergency: family.persons.flatMap((p) =>
      p.chequingAccounts
        .filter((a) => a.emergencyFund)
        .map((a) => ({
          accountUuid: a.uuid,
          wholeBalance: !!a.emergencyFund?.wholeBalance,
          amount: a.emergencyFund?.amount,
        })),
    ),
    pensions: pensionsFromFamily(family),
    retirement: retirementFromFamily(family),
    firstTimeBuyer: Object.fromEntries(family.persons.map((p) => [p.uuid, p.isFirstTimeHomeBuyer])),
    oasYears: Object.fromEntries(
      family.persons.map((p) => [p.uuid, p.oasResidency?.yearsInCanadaAfter18]),
    ),
  };
}

const IMPORTED = new Set([
  ValueSource_Source.CSV,
  ValueSource_Source.FLINKS,
  ValueSource_Source.WEALTHICA,
]);

/** "Updates itself" only where a value was actually imported (field_sources). */
function importedSections(family: Family): Set<SectionKey> {
  const out = new Set<SectionKey>();
  Object.entries(family.fieldSources).forEach(([path, src]) => {
    if (!IMPORTED.has(src.source)) return;
    if (/_accounts\[/.test(path)) out.add("accounts");
    if (/credit_cards\[|loans\[|mortgages\[|properties\[/.test(path)) out.add("debts");
    if (/expenses\[/.test(path)) out.add("spending");
    if (/jobs\[|untaxed_incomes\[/.test(path)) out.add("income");
  });
  return out;
}

function Cat({
  k,
  open,
  onToggle,
  icon,
  title,
  sub,
  fed,
  children,
}: {
  k: string;
  open: boolean;
  onToggle: () => void;
  icon: string;
  title: string;
  sub: string;
  fed?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={`edcat${open ? " on" : ""}`} data-testid={`ud-${k}`}>
      <button type="button" className="edhead" onClick={onToggle} aria-expanded={open}>
        <span className="ic">{icon}</span>
        <span style={{ flex: 1 }}>
          <b>{title}</b>
          <span className="sub">{sub}</span>
        </span>
        {fed && <span className="fedtag">Updates itself</span>}
        <span className="cv" />
      </button>
      {open && <div className="edbody act-body ud-body">{children}</div>}
    </div>
  );
}

const MARITAL: [MaritalStatus, string][] = [
  [MaritalStatus.SINGLE, "Single"],
  [MaritalStatus.MARRIED, "Married"],
  [MaritalStatus.COMMON_LAW, "Living common-law"],
];
/** The account-kind select's value for a group RRSP through work (kind RRSP, plan type group). */
const GROUP_RRSP = -1;
const ACCOUNT_TYPES: [number, string][] = [
  AccountType.CHEQUING_ACCOUNT,
  AccountType.TFSA_ACCOUNT,
  AccountType.RRSP_ACCOUNT,
  GROUP_RRSP,
  AccountType.FHSA_ACCOUNT,
  AccountType.RESP_ACCOUNT,
  AccountType.NON_REGISTERED_INVESTMENT_ACCOUNT,
  AccountType.RRIF_ACCOUNT,
  AccountType.DC_PENSION_ACCOUNT,
].map((t) => [
  t,
  t === GROUP_RRSP ? "Group RRSP (through work)" : ACCOUNT_TYPE_LABEL[t as AccountType],
]);
const isGroupRrsp = (a: AccountsSection_Account) =>
  a.kind === AccountType.RRSP_ACCOUNT && a.planType === RRSPAccount_PlanType.GROUP;
const kindPatch = (v: number): Partial<AccountsSection_Account> =>
  v === GROUP_RRSP
    ? { kind: AccountType.RRSP_ACCOUNT, planType: RRSPAccount_PlanType.GROUP }
    : {
        kind: v as AccountType,
        planType:
          v === AccountType.RRSP_ACCOUNT
            ? RRSPAccount_PlanType.INDIVIDUAL
            : RRSPAccount_PlanType.UNSPECIFIED,
      };
const PENSION_TYPES: [PensionType, string][] = [
  [PensionType.DEFINED_BENEFIT, "Defined benefit"],
  [PensionType.DEFINED_CONTRIBUTION, "Defined contribution"],
  [PensionType.NO_PENSION, "None"],
];

const FLUSH = { margin: 0 } as const;

const at = <T,>(list: T[], i: number, patch: Partial<T>): T[] =>
  list.map((x, k) => (k === i ? { ...x, ...patch } : x));

function Body({ ctx }: { ctx: PlanCtx }) {
  const { family, doc, today } = ctx;
  const save = useFamilySave(doc);
  const rebuilding = isRebuilding(doc, ctx.stored);
  const [model, setModel] = useState<Model>(() => modelFrom(family));
  const [dirty, setDirty] = useState<Set<SectionKey>>(new Set());
  // The "What you owe" checks onboarding makes too (questionnaire/owed.ts), by row path.
  const [owedErrors, setOwedErrors] = useState<Record<string, string>>({});
  // `?open=debts`: a section to open; `&show=<test id>`: what in it to bring into view (the FHSA
  // dialog's "Let me fix my data" and the FHSA warnings: `?open=debts&show=ud-first-time`).
  const [params] = useSearchParams();
  // Each navigation's own key: following the same link again (the dialog's "Let me fix my data"
  // once the address already asks for the boxes) keeps `params`, but should open and scroll again.
  const { key: navigation } = useLocation();
  const [open, setOpen] = useState<string | null>(() => params.get("open"));
  // What to scroll to, once the section just opened has rendered and the one open before it has
  // collapsed (scrolling first leaves the target's header above the viewport on a phone). A new
  // object each time, so asking for the same target again scrolls again.
  const [reveal, setReveal] = useState<{ target: string } | null>(null);
  const show = (section: string | null, target = `ud-${section}`) => {
    if (section) setOpen(section);
    setReveal({ target });
  };
  useEffect(() => {
    if (!reveal) return;
    const frame = requestAnimationFrame(() =>
      document
        .querySelector(`[data-testid="${reveal.target}"]`)
        ?.scrollIntoView?.({ block: "start" }),
    );
    return () => cancelAnimationFrame(frame);
  }, [reveal]);
  useEffect(() => {
    const section = params.get("open");
    if (!section) return;
    setOpen(section);
    setReveal({ target: params.get("show") ?? `ud-${section}` });
  }, [params, navigation]);
  const fed = useMemo(() => importedSections(family), [family]);
  // The failed state, once nothing is waiting to be saved. Its "Try again" is the failed-update banner's,
  // at the top of the page (decided by Ian, 2026-10-31: one "Try again" on Update Data).
  // With no plan yet, the failed first plan (NoPlan, above) counts too.
  const lastJob = useLastJob();
  const failed =
    !dirty.size &&
    (ctx.hasPlan === false
      ? lastJob?.state === PlanJob_State.FAILED
      : rebuildFailed(doc, ctx.stored));

  const edit = <K extends keyof Model>(section: SectionKey, key: K, value: Model[K]) => {
    setModel((m) => ({ ...m, [key]: value }));
    setDirty((d) => new Set(d).add(section));
  };
  const toggle = (k: string) => setOpen(open === k ? null : k);

  // A newer version of the household (saved here, elsewhere, or a 409) replaces the form. From Home's
  // contribution action (`?job=&rate=`), the first one also fills in the advised rate, unsaved.
  const focusJob = params.get("job");
  const prefillVersion = useRef<number>(undefined); // the version it was applied to (effects can rerun)
  const [prefilled, setPrefilled] = useState<string | null>(null); // the job whose rate was filled in
  useEffect(() => {
    let next = modelFrom(family);
    const changed = new Set<SectionKey>();
    const i = next.incomes.findIndex((inc) => inc.uuid === focusJob && inc.groupRrspPlan);
    const rate = params.get("rate") ? Number(params.get("rate")) : NaN;
    const first = prefillVersion.current === undefined || prefillVersion.current === doc.version;
    setPrefilled(null);
    if (first && i >= 0) {
      prefillVersion.current = doc.version;
      const plan = next.incomes[i].groupRrspPlan!;
      if (rate >= 0 && rate <= 1 && rate !== plan.employeeContributionRate) {
        setPrefilled(focusJob);
        next = {
          ...next,
          incomes: at(next.incomes, i, {
            groupRrspPlan: { ...plan, employeeContributionRate: rate },
          }),
        };
        changed.add("income");
      }
      show("income", `ud-match-${i}-own`);
    }
    setModel(next);
    setDirty(changed);
    setOwedErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.version]);

  const members = model.household.members;
  const memberOpts: [string, string][] = members.map((m, i) => [
    m.uuid,
    m.givenName || (i ? "Your partner" : "You"),
  ]);
  // Which matched job pays into each group RRSP, as the engine links them on save.
  const groupRrsps = (member: string) => {
    const accounts = model.accounts.filter((a) => a.memberUuid === member && isGroupRrsp(a));
    const jobs = model.incomes
      .filter((inc) => inc.memberUuid === member && inc.groupRrspPlan)
      .map((inc) => ({
        uuid: inc.uuid,
        label: inc.occupation || "your job",
        groupRrspUuid: inc.groupRrspPlan!.groupRrspUuid,
      }));
    return {
      accounts,
      jobs,
      links: groupRrspLinks(
        accounts.map((a) => a.uuid),
        jobs,
      ),
    };
  };
  // accounts another job pays into aren't offered
  const matchAccountChoice = (member: string, job: string): [string, string][] | undefined => {
    const { accounts, jobs } = groupRrsps(member);
    const taken = new Set(jobs.filter((j) => j.uuid !== job).map((j) => j.groupRrspUuid));
    return groupRrspChoiceNeeded(accounts.length, jobs.length)
      ? accounts
          .filter((a) => !taken.has(a.uuid))
          .map((a) => [a.uuid, a.name || `Group RRSP (${money(a.balance)})`])
      : undefined;
  };
  const kids = model.household.children;
  // A member's jobs (employment or self-employment), the incomes a pension can be attached to.
  const jobsOf = (member: string): [string, string][] =>
    model.incomes
      .filter((inc) => inc.memberUuid === member && isJobKind(inc.kind))
      .map((inc, k) => [inc.uuid, inc.name.trim() || inc.occupation.trim() || `Job ${k + 1}`]);

  // A job's start date: not before the person's 14th birthday, and for a job with a defined benefit
  // pension (asked "When did you start at ...?" under "Retirement through work") not after today, as
  // the engine checks it. By income uuid.
  const who = (member: string) => {
    const i = members.findIndex((m) => m.uuid === member);
    return i === 0 ? "you" : members[i]?.givenName.trim() || "your partner";
  };
  const dbJobs = new Set(
    model.pensions.filter((pn) => pn.type === PensionType.DEFINED_BENEFIT).map((pn) => pn.jobUuid),
  );
  const startIssues: Record<string, string | undefined> = Object.fromEntries(
    model.incomes
      .filter((inc) => isJobKind(inc.kind) && inc.startDate?.year)
      .map((inc) => [
        inc.uuid,
        jobStartIssue(
          jobStartFrom(inc.startDate),
          members.find((m) => m.uuid === inc.memberUuid)?.dob,
          today,
          who(inc.memberUuid),
          dbJobs.has(inc.uuid),
        ),
      ]),
  );

  // The retirement age (one for the household) must still be ahead of every adult: the age they
  // picked, or Abi's default (65) when they didn't, which is then asked for too. Checked on what
  // would be saved (edited birthdays and age together), so wrong data can always be corrected.
  const r = model.retirement;
  const target = r.method === RetirementMethod.TARGET;
  // the household's own age (stored, or picked here) is checked and sent like a target's; Abi's
  // default is only when none was ever given
  const ownAge = target || r.age !== undefined;
  const adults: SavingAdult[] = members.map((m, i) => ({
    name: i ? m.givenName.trim() || "Your partner" : "",
    dob: m.dob,
    storedAge: family.persons.find((p) => p.uuid === m.uuid)?.retirementGoal?.retirementAge,
  }));
  const {
    passed,
    askAge,
    age,
    issue: ageIssue,
  } = householdRetirementCheck(
    adults,
    { target: ownAge, age: r.age, storedAge: retirementFromFamily(family).age },
    today,
  );
  // While an age has passed for someone, a household save sends the age shown too: the check above
  // is then exactly what's saved, and every adult (a partner added since, say) gets that age.
  const sendsRetirement = dirty.has("retirement") || (passed && dirty.has("household"));
  // Years in Canada since 18, checked against the birthdays being saved (as in Sharpen).
  const oasIssues: Record<string, string | undefined> = Object.fromEntries(
    family.persons.map((p) => [
      p.uuid,
      yearsInCanadaIssue(
        model.oasYears[p.uuid],
        members.find((m) => m.uuid === p.uuid)?.dob ?? p.dob,
        today,
      ),
    ]),
  );

  const submit = async () => {
    if (ageIssue && (dirty.has("retirement") || dirty.has("household"))) {
      show(null, "ud-method");
      return;
    }
    if (dirty.has("oas") && Object.values(oasIssues).some(Boolean)) {
      show("retirement");
      return;
    }
    if (dirty.has("income")) {
      const bad = model.incomes.find((inc) => startIssues[inc.uuid]);
      if (bad) {
        const pension = model.pensions.findIndex(
          (pn) => pn.type === PensionType.DEFINED_BENEFIT && pn.jobUuid === bad.uuid,
        );
        if (pension >= 0) show("work", `ud-pension-${pension}-start`);
        else show("income");
        return;
      }
    }
    const q = emptyQuestionnaire(today);
    if (dirty.has("household")) q.household = model.household;
    if (dirty.has("income")) q.income = { incomes: model.incomes };
    if (dirty.has("accounts")) q.accounts = { accounts: model.accounts };
    if (dirty.has("buyer"))
      family.persons
        .filter((p) => (model.firstTimeBuyer[p.uuid] ?? false) !== p.isFirstTimeHomeBuyer)
        .forEach((p) =>
          q.fieldEdits.push(
            ...firstTimeBuyerEdits(
              p.uuid,
              model.firstTimeBuyer[p.uuid] ?? false,
              // their FHSAs whose move was confirmed, unless this save removes them or moves them
              // to someone else
              p.fhsaAccounts
                .filter(
                  (f) =>
                    f.transferToRrspConfirmed &&
                    (!dirty.has("accounts") ||
                      model.accounts.some((a) => a.uuid === f.uuid && a.memberUuid === p.uuid)),
                )
                .map((f) => f.uuid),
            ),
          ),
        );
    if (dirty.has("debts")) {
      const issues = validateOwed(model.properties, model.debts, today);
      setOwedErrors(Object.fromEntries(issues.map((x) => [x.path, x.message])));
      if (issues.length) {
        show("debts");
        return;
      }
      q.debts = { debts: model.debts.map((d) => debtAnswer(d, members[0]?.uuid ?? "")) };
      q.properties = { properties: model.properties.map(propertyAnswer) };
    }
    if (dirty.has("oas")) {
      // Only values given: a blank stays unset (the engine assumes a full lifetime here).
      family.persons
        .filter((p) => {
          const v = model.oasYears[p.uuid];
          return v !== undefined && v !== p.oasResidency?.yearsInCanadaAfter18;
        })
        .forEach((p) =>
          q.fieldEdits.push(fieldEdit(paths.oasYears(p.uuid), model.oasYears[p.uuid]!)),
        );
    }
    if (dirty.has("spending")) q.spending = model.spending;
    if (dirty.has("safety") || dirty.has("accounts"))
      q.safetyNet = { emergencyFunds: model.emergency, pensions: model.pensions };
    if (sendsRetirement)
      q.retirement =
        model.retirement.method === RetirementMethod.TARGET
          ? {
              method: RetirementMethod.TARGET,
              incomeReplacementPercent: model.retirement.pct ?? DEFAULT_RETIREMENT_PCT,
              retirementAge: age,
            }
          : askAge
            ? { method: RetirementMethod.SPENDING, retirementAge: age }
            : { method: RetirementMethod.SPENDING };
    if (
      await save.run(q, null) // the save bar shows the rebuild
    )
      setDirty(new Set());
  };

  return (
    <>
      <div className="card pad" style={{ marginBottom: 16 }} data-testid="ud-method">
        <h2 style={{ marginBottom: 4 }}>Retiring comfortably</h2>
        <p className="mut" style={{ marginBottom: 14, maxWidth: "62ch" }}>
          What you&rsquo;d want each year once you stop working, as a share of today&rsquo;s income,
          and the age you&rsquo;d want to retire. Priorities asks the same.
          {!target && " Your plan works it out from what you spend today until you pick a share."}
        </p>
        <div className="act-body">
          <div className="two" style={{ maxWidth: 640 }}>
            <SelectField
              label="Share of today’s income you’d want at retirement"
              value={target ? (r.pct ?? DEFAULT_RETIREMENT_PCT) : 0}
              options={[
                // a household from before v39 keeps "from your spending" until it picks a share
                ...(retirementFromFamily(family).method !== RetirementMethod.TARGET
                  ? ([[0, FROM_SPENDING]] as [number, string][])
                  : []),
                ...PCT_OPTIONS.map((p): [number, string] => [p, `${p}%`]),
              ]}
              onChange={(pct) =>
                edit(
                  "retirement",
                  "retirement",
                  pct
                    ? { method: RetirementMethod.TARGET, pct, age: r.age ?? age }
                    : { ...r, method: RetirementMethod.SPENDING },
                )
              }
              style={{ margin: 0 }}
              testId="ud-pct"
            />
            <SelectField
              label="Age you’d want to retire"
              value={age}
              options={retirementAgeOptions(adults, today).map((a) => [a, String(a)])}
              onChange={(a) => edit("retirement", "retirement", { ...r, age: a })}
              style={{ margin: 0 }}
              testId="ud-age"
              error={ageIssue}
              hint={
                !askAge
                  ? `Abi plans around retiring at ${DEFAULT_RETIREMENT_AGE} unless you pick an age.`
                  : undefined
              }
            />
          </div>
        </div>
      </div>

      <div id="ed-list">
        <Cat
          k="people"
          open={open === "people"}
          onToggle={() => toggle("people")}
          icon="👤"
          title="You and your household"
          sub="Names, birthdays, where you live, who depends on you"
        >
          {members.map((m, i) => (
            <div className="rowbox ud-wide" key={m.uuid}>
              <div className="rowhead">
                <b>{i ? "Your partner" : "You"}</b>
              </div>
              <div className="three">
                <div className="field" style={{ margin: 0 }}>
                  <label htmlFor={`ud-gn-${i}`}>First name</label>
                  <input
                    id={`ud-gn-${i}`}
                    value={m.givenName}
                    onChange={(e) =>
                      edit("household", "household", {
                        ...model.household,
                        members: at(members, i, { givenName: e.target.value }),
                      })
                    }
                  />
                </div>
                <div className="field" style={{ margin: 0 }}>
                  <label htmlFor={`ud-fn-${i}`}>Last name</label>
                  <input
                    id={`ud-fn-${i}`}
                    value={m.familyName}
                    onChange={(e) =>
                      edit("household", "household", {
                        ...model.household,
                        members: at(members, i, { familyName: e.target.value }),
                      })
                    }
                  />
                </div>
                <div className="field" style={{ margin: 0 }}>
                  <label>Date of birth</label>
                  <DatePicker
                    value={m.dob}
                    mode="past"
                    day
                    placeholder="Pick a birthday"
                    onChange={(dob) =>
                      edit("household", "household", {
                        ...model.household,
                        members: at(members, i, { dob }),
                      })
                    }
                  />
                </div>
              </div>
            </div>
          ))}
          <SelectField
            label="Marital status"
            value={model.household.maritalStatus || MaritalStatus.SINGLE}
            options={MARITAL}
            onChange={(maritalStatus) =>
              edit("household", "household", { ...model.household, maritalStatus })
            }
          />
          <SelectField
            label="Province"
            value={model.household.province}
            options={PROVINCES}
            onChange={(province) =>
              edit("household", "household", { ...model.household, province })
            }
          />
          {kids.map((k, i) => (
            <div className="rowbox ud-wide" key={k.uuid}>
              <div className="rowhead">
                <b>Child {i + 1}</b>
                <button
                  type="button"
                  className="rm"
                  onClick={() =>
                    edit("household", "household", {
                      ...model.household,
                      children: kids.filter((_, x) => x !== i),
                    })
                  }
                >
                  Remove
                </button>
              </div>
              <div className="two">
                <div className="field" style={{ margin: 0 }}>
                  <label htmlFor={`ud-kid-${i}`}>First name</label>
                  <input
                    id={`ud-kid-${i}`}
                    value={k.givenName}
                    onChange={(e) =>
                      edit("household", "household", {
                        ...model.household,
                        children: at(kids, i, { givenName: e.target.value }),
                      })
                    }
                  />
                </div>
                <div className="field" style={{ margin: 0 }}>
                  <label>Date of birth</label>
                  <DatePicker
                    value={k.dob}
                    mode="past"
                    day
                    placeholder="Pick a birthday"
                    onChange={(dob) =>
                      edit("household", "household", {
                        ...model.household,
                        children: at(kids, i, { dob }),
                      })
                    }
                  />
                </div>
              </div>
            </div>
          ))}
          <button
            type="button"
            className="mini2 mini"
            onClick={() =>
              edit("household", "household", {
                ...model.household,
                children: [
                  ...kids,
                  { uuid: newUuid(), givenName: "", familyName: "", dob: undefined },
                ],
              })
            }
          >
            + Add a child
          </button>
        </Cat>

        <Cat
          k="income"
          open={open === "income"}
          onToggle={() => toggle("income")}
          icon="💼"
          title="What you earn"
          sub="Occupation, when each job started, and what each one pays before tax"
          fed={fed.has("income")}
        >
          {model.incomes.map((inc, i) => (
            <div className="rowbox ud-wide" key={inc.uuid}>
              <div className="rowhead">
                <b>
                  {memberOpts.find(([u]) => u === inc.memberUuid)?.[1] ?? "Income"} &middot;{" "}
                  {inc.name.trim() || "income"}
                </b>
                <button
                  type="button"
                  className="rm"
                  onClick={() =>
                    edit(
                      "income",
                      "incomes",
                      model.incomes.filter((_, x) => x !== i),
                    )
                  }
                >
                  Remove
                </button>
              </div>
              <div className="three">
                <SelectField
                  label="Whose"
                  value={inc.memberUuid}
                  options={memberOpts}
                  onChange={(memberUuid) =>
                    edit("income", "incomes", at(model.incomes, i, { memberUuid }))
                  }
                  style={{ margin: 0 }}
                />
                <SelectField
                  label="What kind"
                  value={incomeType(inc.kind)}
                  options={INCOME_TYPE_LABEL}
                  onChange={(t) => {
                    const kind = kindForType(t, inc.kind);
                    edit(
                      "income",
                      "incomes",
                      at(model.incomes, i, {
                        kind,
                        groupRrspPlan: MATCHABLE_KINDS.has(kind) ? inc.groupRrspPlan : undefined,
                      }),
                    );
                  }}
                  hint={INCOME_TYPE_HINT[incomeType(inc.kind)]}
                  testId={`ud-income-${i}-type`}
                  style={{ margin: 0 }}
                />
                <MoneyField
                  label="Before tax, a year"
                  value={inc.annualAmountBeforeTax}
                  onChange={(v) =>
                    edit(
                      "income",
                      "incomes",
                      at(model.incomes, i, { annualAmountBeforeTax: v ?? 0 }),
                    )
                  }
                  style={{ margin: 0 }}
                />
              </div>
              <div className="three" style={{ marginTop: 12 }}>
                <TextField
                  label="Name of income source"
                  value={inc.name}
                  placeholder="e.g. Day job"
                  onChange={(name) => edit("income", "incomes", at(model.incomes, i, { name }))}
                  testId={`ud-income-${i}-name`}
                  style={{ margin: 0 }}
                />
                <div className="field" style={{ margin: 0 }}>
                  <label htmlFor={`ud-occ-${i}`}>Occupation</label>
                  <input
                    id={`ud-occ-${i}`}
                    value={inc.occupation}
                    placeholder="Not yet given"
                    onChange={(e) =>
                      edit(
                        "income",
                        "incomes",
                        at(model.incomes, i, { occupation: e.target.value }),
                      )
                    }
                  />
                </div>
                <NumberField
                  label="Year you started"
                  value={inc.startDate?.year || undefined}
                  onChange={(y) =>
                    edit(
                      "income",
                      "incomes",
                      at(model.incomes, i, {
                        // the month (and day) on file stay with a new year
                        startDate: y ? { month: 0, day: 0, ...inc.startDate, year: y } : undefined,
                      }),
                    )
                  }
                  placeholder="Not yet given"
                  error={startIssues[inc.uuid]}
                  testId={`ud-income-${i}-start`}
                  style={{ margin: 0 }}
                />
              </div>
              {MATCHABLE_KINDS.has(inc.kind) && (
                <div className={inc.uuid === prefilled ? "wp-focus" : undefined}>
                  <GroupRrspOfferField
                    offer={inc.groupRrspPlan}
                    salary={inc.annualAmountBeforeTax}
                    onChange={(groupRrspPlan) =>
                      edit("income", "incomes", at(model.incomes, i, { groupRrspPlan }))
                    }
                    testId={`ud-match-${i}`}
                    accounts={matchAccountChoice(inc.memberUuid, inc.uuid)}
                  />
                  {inc.uuid === prefilled && (
                    <p className="hint" data-testid="ud-match-focus">
                      Once you&rsquo;ve changed it at work, save this so your plan knows.
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
          <button
            type="button"
            className="mini2 mini"
            onClick={() =>
              edit("income", "incomes", [
                ...model.incomes,
                {
                  uuid: newUuid(),
                  memberUuid: members[0]?.uuid ?? "",
                  kind: IncomeKind.SALARY,
                  name: "",
                  annualAmountBeforeTax: 0,
                  occupation: "",
                },
              ])
            }
          >
            + Add an income
          </button>
        </Cat>

        <Cat
          k="work"
          open={open === "work"}
          onToggle={() => toggle("work")}
          icon="🏦"
          title="Retirement through work"
          sub="Pension type, group plans, employer match"
        >
          {model.pensions.length === 0 && (
            <p className="mut ud-wide">No workplace pension. Add one if that&rsquo;s changed.</p>
          )}
          {model.pensions.map((pn, i) => (
            <div className="rowbox ud-wide" key={pn.uuid}>
              <div className="rowhead">
                <b>Workplace pension</b>
                <button
                  type="button"
                  className="rm"
                  onClick={() =>
                    edit(
                      "safety",
                      "pensions",
                      model.pensions.filter((_, x) => x !== i),
                    )
                  }
                >
                  Remove
                </button>
              </div>
              <div className="two">
                <SelectField
                  label="Whose"
                  value={pn.memberUuid}
                  options={memberOpts}
                  onChange={(memberUuid) =>
                    edit("safety", "pensions", at(model.pensions, i, { memberUuid }))
                  }
                  style={{ margin: 0 }}
                />
                <SelectField
                  label="Which kind"
                  value={pn.type}
                  options={PENSION_TYPES}
                  onChange={(type) => edit("safety", "pensions", at(model.pensions, i, { type }))}
                  style={{ margin: 0 }}
                />
              </div>
              <SelectField
                label="Which job or income is it attached to?"
                value={jobsOf(pn.memberUuid).some(([u]) => u === pn.jobUuid) ? pn.jobUuid : ""}
                options={[["", "Not linked to a job"], ...jobsOf(pn.memberUuid)]}
                onChange={(jobUuid) =>
                  edit("safety", "pensions", at(model.pensions, i, { jobUuid }))
                }
                hint="A defined benefit plan is worked out from the salary it sits against."
                testId={`ud-pension-${i}-job`}
                style={{ margin: "12px 0 0" }}
              />
              {pn.type === PensionType.DEFINED_BENEFIT &&
                (() => {
                  const j = model.incomes.findIndex(
                    (inc) =>
                      inc.uuid === pn.jobUuid &&
                      inc.memberUuid === pn.memberUuid &&
                      isJobKind(inc.kind),
                  );
                  if (j < 0) return null;
                  const inc = model.incomes[j];
                  return (
                    <JobStartField
                      question={jobStartQuestion(
                        who(inc.memberUuid),
                        inc.name.trim() || inc.occupation.trim(),
                      )}
                      value={jobStartFrom(inc.startDate)}
                      onChange={(start) =>
                        edit(
                          "income",
                          "incomes",
                          at(model.incomes, j, { startDate: jobStartDate(start) }),
                        )
                      }
                      error={startIssues[inc.uuid]}
                      testId={`ud-pension-${i}-start`}
                      style={{ marginTop: 12 }}
                    />
                  );
                })()}
            </div>
          ))}
          <button
            type="button"
            className="mini2 mini"
            onClick={() =>
              edit("safety", "pensions", [
                ...model.pensions,
                {
                  uuid: newUuid(),
                  memberUuid: members[0]?.uuid ?? "",
                  type: PensionType.DEFINED_BENEFIT,
                  jobUuid: "",
                },
              ])
            }
          >
            + Add a workplace pension
          </button>
          <Link className="mini2 mini" to="/app/sharpen">
            Accrual rate, years of service and employer match are on Sharpen your plan &rarr;
          </Link>
        </Cat>

        <Cat
          k="accounts"
          open={open === "accounts"}
          onToggle={() => toggle("accounts")}
          icon="📈"
          title="Accounts and balances"
          sub="Chequing, savings and investments, and your rainy-day fund"
          fed={fed.has("accounts")}
        >
          {model.accounts.map((a, i) => {
            const ef = model.emergency.find((e) => e.accountUuid === a.uuid);
            const setEf = (next: SafetyNetSection_EmergencyFund | null) =>
              edit("accounts", "emergency", [
                ...model.emergency.filter((e) => e.accountUuid !== a.uuid),
                ...(next ? [next] : []),
              ]);
            const match = isGroupRrsp(a) ? groupRrsps(a.memberUuid).links.get(a.uuid) : undefined;
            return (
              <div className="rowbox ud-wide" key={a.uuid} data-testid={`ud-account-${i}`}>
                <div className="rowhead">
                  <b>{a.name || (isGroupRrsp(a) ? "Group RRSP" : ACCOUNT_TYPE_LABEL[a.kind])}</b>
                  <button
                    type="button"
                    className="rm"
                    onClick={() =>
                      edit(
                        "accounts",
                        "accounts",
                        model.accounts.filter((_, x) => x !== i),
                      )
                    }
                  >
                    Remove
                  </button>
                </div>
                <div className="two">
                  <SelectField
                    label="What kind"
                    value={isGroupRrsp(a) ? GROUP_RRSP : a.kind}
                    options={ACCOUNT_TYPES}
                    onChange={(v) =>
                      edit("accounts", "accounts", at(model.accounts, i, kindPatch(v)))
                    }
                    style={FLUSH}
                  />
                  <TextField
                    label="Name (optional)"
                    value={a.name}
                    placeholder={`e.g. Simplii ${ACCOUNT_TYPE_LABEL[a.kind]}`}
                    onChange={(name) =>
                      edit("accounts", "accounts", at(model.accounts, i, { name }))
                    }
                    testId={`ud-account-${i}-name`}
                    style={FLUSH}
                  />
                </div>
                <div className="two" style={{ marginTop: 12 }}>
                  <SelectField
                    label="Whose"
                    value={a.memberUuid}
                    options={memberOpts}
                    onChange={(memberUuid) =>
                      edit("accounts", "accounts", at(model.accounts, i, { memberUuid }))
                    }
                    style={{ margin: 0 }}
                  />
                  <MoneyField
                    label="Balance today"
                    value={a.balance}
                    onChange={(v) =>
                      edit("accounts", "accounts", at(model.accounts, i, { balance: v ?? 0 }))
                    }
                    style={{ margin: 0 }}
                    testId={`ud-account-${i}-balance`}
                  />
                </div>
                {isGroupRrsp(a) && (
                  <p className="hint" data-testid={`ud-account-${i}-match`}>
                    {match
                      ? `Your employer’s match from ${match.label} pays into this.`
                      : "No employer match pays into this, so Abi treats it like any RRSP."}
                  </p>
                )}
                {a.kind === AccountType.CHEQUING_ACCOUNT && (
                  <div className="two" style={{ marginTop: 12 }}>
                    <SelectField
                      label="Set aside for emergencies"
                      value={ef ? (ef.wholeBalance ? "all" : "part") : "none"}
                      options={[
                        ["none", "None of it"],
                        ["all", "All of it"],
                        ["part", "Just part of it"],
                      ]}
                      onChange={(v) =>
                        setEf(
                          v === "none"
                            ? null
                            : {
                                accountUuid: a.uuid,
                                wholeBalance: v === "all",
                                amount: v === "part" ? ef?.amount : undefined,
                              },
                        )
                      }
                      style={{ margin: 0 }}
                    />
                    {ef && !ef.wholeBalance && (
                      <MoneyField
                        label="How much"
                        value={ef.amount}
                        onChange={(amount) => setEf({ ...ef, amount })}
                        style={{ margin: 0 }}
                      />
                    )}
                  </div>
                )}
                {a.kind === AccountType.RESP_ACCOUNT && kids.length > 0 && (
                  <div className="field" style={{ margin: "12px 0 0" }}>
                    <label>Who is it for?</label>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {kids.map((k) => {
                        const on = a.beneficiaryUuids.includes(k.uuid);
                        return (
                          <button
                            type="button"
                            key={k.uuid}
                            className={`chip${on ? " on" : ""}`}
                            aria-pressed={on}
                            onClick={() =>
                              edit(
                                "accounts",
                                "accounts",
                                at(model.accounts, i, {
                                  beneficiaryUuids: on
                                    ? a.beneficiaryUuids.filter((u) => u !== k.uuid)
                                    : [...a.beneficiaryUuids, k.uuid],
                                }),
                              )
                            }
                          >
                            {k.givenName || "Your child"}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {members
            .filter((m) =>
              model.accounts.some(
                (a) => a.kind === AccountType.FHSA_ACCOUNT && a.memberUuid === m.uuid,
              ),
            )
            .map((m, i) => {
              const you = members[0]?.uuid === m.uuid;
              const name = m.givenName || (you ? "" : "Your partner");
              const firstTime = model.firstTimeBuyer[m.uuid] ?? false;
              return (
                <p
                  className="hint"
                  key={m.uuid}
                  style={{ marginTop: 10 }}
                  data-testid={`ud-fhsa-buyer-${i}`}
                >
                  {copyText("fields.firstTimeBuyer.fhsaRule")}{" "}
                  {name
                    ? copyText(
                        firstTime
                          ? "fields.firstTimeBuyer.markedNamed"
                          : "fields.firstTimeBuyer.notMarkedNamed",
                        { name },
                      )
                    : copyText(
                        firstTime
                          ? "fields.firstTimeBuyer.markedYou"
                          : "fields.firstTimeBuyer.notMarkedYou",
                      )}{" "}
                  <button
                    type="button"
                    className="linkbtn"
                    onClick={() => show("debts", FIRST_TIME_BOXES)}
                  >
                    {copyText("fields.firstTimeBuyer.change")}
                  </button>
                </p>
              );
            })}
          <button
            type="button"
            className="mini2 mini"
            onClick={() =>
              edit("accounts", "accounts", [
                ...model.accounts,
                {
                  uuid: newUuid(),
                  kind: AccountType.TFSA_ACCOUNT,
                  savings: false,
                  name: "",
                  memberUuid: members[0]?.uuid ?? "",
                  balance: 0,
                  beneficiaryUuids: [],
                  planType: RRSPAccount_PlanType.UNSPECIFIED,
                },
              ])
            }
          >
            + Add an account
          </button>
        </Cat>

        <Cat
          k="debts"
          open={open === "debts"}
          onToggle={() => toggle("debts")}
          icon="💳"
          title="What you owe"
          sub="Balances, rates and terms. Rates are the part that goes stale."
          fed={fed.has("debts")}
        >
          {model.properties.map((p, i) => (
            <PropertyRow
              key={p.uuid}
              row={p}
              i={i}
              prefix="ud-"
              className="rowbox ud-wide"
              fieldStyle={FLUSH}
              err={(path) => owedErrors[path]}
              onChange={(change) => edit("debts", "properties", at(model.properties, i, change))}
              onRemove={() =>
                edit(
                  "debts",
                  "properties",
                  model.properties.filter((_, x) => x !== i),
                )
              }
            />
          ))}
          <button
            type="button"
            className="mini2 mini"
            onClick={() =>
              edit("debts", "properties", [
                ...model.properties,
                newProperty(!model.properties.length),
              ])
            }
          >
            + Add a property
          </button>
          <FirstTimeBuyerChecks
            adults={members.filter((m) => family.persons.some((p) => p.uuid === m.uuid))}
            value={model.firstTimeBuyer}
            onChange={(uuid, v) =>
              edit("buyer", "firstTimeBuyer", { ...model.firstTimeBuyer, [uuid]: v })
            }
            prefix="ud-"
          />
          {model.debts.map((d, i) => (
            <DebtRow
              key={d.uuid}
              row={d}
              i={i}
              prefix="ud-"
              className="rowbox ud-wide"
              fieldStyle={FLUSH}
              err={(path) => owedErrors[path]}
              onChange={(change) => edit("debts", "debts", at(model.debts, i, change))}
              onRemove={() =>
                edit(
                  "debts",
                  "debts",
                  model.debts.filter((_, x) => x !== i),
                )
              }
            />
          ))}
          <button
            type="button"
            className="mini2 mini"
            onClick={() => edit("debts", "debts", [...model.debts, newDebt()])}
          >
            + Add a debt
          </button>
        </Cat>

        <Cat
          k="spending"
          open={open === "spending"}
          onToggle={() => toggle("spending")}
          icon="🛒"
          title="What you spend"
          sub="A monthly figure for essentials, lifestyle and rent"
          fed={fed.has("spending")}
        >
          <MoneyField
            label="Essentials, a month"
            value={model.spending.essentialMonthly}
            onChange={(v) =>
              edit("spending", "spending", { ...model.spending, essentialMonthly: v ?? 0 })
            }
            testId="ud-essential"
          />
          <MoneyField
            label="Lifestyle, a month"
            value={model.spending.lifestyleMonthly}
            onChange={(v) =>
              edit("spending", "spending", { ...model.spending, lifestyleMonthly: v ?? 0 })
            }
          />
          <MoneyField
            label="Rent, a month"
            value={model.spending.rentMonthly}
            onChange={(rentMonthly) =>
              edit("spending", "spending", { ...model.spending, rentMonthly })
            }
            placeholder="0"
          />
          <p className="mut ud-wide">
            Every month:{" "}
            {money(
              model.spending.essentialMonthly +
                model.spending.lifestyleMonthly +
                (model.spending.rentMonthly ?? 0),
            )}
          </p>
        </Cat>

        <Cat
          k="priorities"
          open={open === "priorities"}
          onToggle={() => toggle("priorities")}
          icon="🎯"
          title="Your priorities"
          sub="What matters, how much, and by when"
        >
          {family.retirementPriorityLevel > 0 && (
            <label className="edf">
              <span>Retiring comfortably</span>
              <input readOnly value={`${family.retirementPriorityLevel} out of 10`} />
            </label>
          )}
          {family.financialPriorities.map((p) => (
            <label className="edf" key={p.uuid}>
              <span>{p.name}</span>
              <input
                readOnly
                value={`${p.level} out of 10${p.nextDueDate ? `, by ${formatDate(p.nextDueDate)}` : ""}`}
              />
            </label>
          ))}
          {family.homebuyingPriorities.map((p) => (
            <label className="edf" key={p.uuid}>
              <span>{p.name || "Buying a home"}</span>
              <input
                readOnly
                value={`${p.level} out of 10, a ${money(p.housePrice)} home${p.downPayment !== undefined ? ` with ${money(p.downPayment)} down` : ""}${p.dueDate ? `, by ${formatDate(p.dueDate)}` : ""}`}
              />
            </label>
          ))}
          <Link className="mini2 mini" to="/app/priorities/edit">
            Open the full page &rarr;
          </Link>
        </Cat>

        <Cat
          k="retirement"
          open={open === "retirement"}
          onToggle={() => toggle("retirement")}
          icon="🌴"
          title="Retirement"
          sub="Your target, and your years in Canada"
        >
          <label className="edf">
            <span>Method</span>
            <input
              readOnly
              value={
                target
                  ? `${r.pct ?? DEFAULT_RETIREMENT_PCT}% of today’s income, retiring at ${age}`
                  : `Worked out from what you spend, retiring at ${age}`
              }
            />
          </label>
          {family.persons.map((p, i) => (
            <NumberField
              key={p.uuid}
              label={`Years in Canada since 18 — ${p.givenName || (i ? "Your partner" : "You")}`}
              value={model.oasYears[p.uuid]}
              onChange={(n) => edit("oas", "oasYears", { ...model.oasYears, [p.uuid]: n })}
              placeholder="Not yet given"
              hint={copyText("fields.yearsInCanadaHint")}
              error={oasIssues[p.uuid]}
              testId={`ud-oas-${i}`}
            />
          ))}
          <Link className="mini2 mini" to="/app/retirement">
            Open the full page &rarr;
          </Link>
        </Cat>
      </div>

      <div className={`edsave${dirty.size || rebuilding || failed ? " on" : ""}`}>
        <div data-testid="ud-save-status">
          {dirty.size ? (
            <>
              <b>Changes aren&rsquo;t live until you recalculate.</b>
              <span>
                Abi reruns the whole plan against the new numbers. It usually takes under a minute,
                and your actions and dates may move.
              </span>
            </>
          ) : rebuilding ? (
            <>
              <b className="wp-sync">
                <span className="wp-dot" aria-hidden="true" />
                <Copy k="plan.updatingBar" />
              </b>
              <Copy k="plan.updatingBarSub" as="span" />
            </>
          ) : failed && ctx.hasPlan === false ? (
            // the first plan failed: there's no previous plan to fall back on
            <>
              <Copy k="plan.failedFirstBar" as="b" />
              <Copy k="plan.failedFirstBarSub" as="span" />
            </>
          ) : failed ? (
            <>
              <Copy k="plan.failedBar" as="b" />
              <Copy k="plan.failedBarSub" as="span" />
            </>
          ) : (
            <>
              <Copy k="plan.updated" as="b" />
              <Copy k="plan.updatedBarSub" as="span" />
            </>
          )}
          <SaveFeedback state={save} />
        </div>
        {dirty.size > 0 && (
          <button
            type="button"
            className="btn"
            onClick={submit}
            disabled={save.pending}
            data-testid="ud-save"
          >
            {save.pending ? "Saving…" : "Save and recalculate"}
          </button>
        )}
      </div>
    </>
  );
}

/**
 * The soft gate before editing (DEVIATIONS.md "Plan refresh"): a save moves the household's facts date
 * to today, so values the user doesn't touch are taken as today's. When a check-in is pending, the
 * page first offers it, or lets the user say their other numbers are still current. A check-in whose
 * answers don't add up sends the user here past the gate (navigation state `skipCheckIn`).
 */
function CheckInFirst({ ctx, children }: { ctx: PlanCtx; children: ReactNode }) {
  const pending = useMemo(
    () => ctx.hasPlan !== false && checkInPending(ctx.family, ctx.abi, ctx.today),
    [ctx],
  );
  const [skipped, setSkipped] = useState(false);
  const [checking, setChecking] = useState(false);
  const sentPast = skipsCheckIn(useLocation().state);
  if (!pending || skipped || sentPast) return <>{children}</>;
  return (
    <div className="card pad" data-testid="ud-checkin-first">
      <h3>Check in before you edit</h3>
      <p>
        Your numbers are as of {formatDate(ctx.family.factsAsOf)}. Since then Abi has assumed you
        followed your plan. Tell it what actually happened first, so the numbers you don&rsquo;t
        change here stay right.
      </p>
      <div className="row" style={{ gap: 12, marginTop: 12, flexWrap: "wrap" }}>
        <button type="button" className="btn" onClick={() => setChecking(true)}>
          Check in now
        </button>
        <button
          type="button"
          className="btn g"
          onClick={() => setSkipped(true)}
          data-testid="ud-checkin-skip"
        >
          Skip: my other numbers are still current
        </button>
      </div>
      {checking && (
        <CheckInModal
          doc={ctx.doc}
          abi={ctx.abi}
          today={ctx.today}
          onClose={() => setChecking(false)}
        />
      )}
    </div>
  );
}

export function UpdateDataPage() {
  return (
    <PlanPage
      title="pages.updatedata.title"
      sub="pages.updatedata.sub"
      testId="updatedata"
      requirePlan={false}
      retiredBanner="plain"
    >
      {(ctx) => (
        <CheckInFirst ctx={ctx}>
          <Body ctx={ctx} />
        </CheckInFirst>
      )}
    </PlanPage>
  );
}
